import { Injectable, Inject } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, NavigationExtras, ActivatedRoute } from '@angular/router';
import { DOCUMENT } from '@angular/common'; 

/**
 * Redirect service to catch External URLs
 * https://medium.com/wizdm-genesys/angular-redirecting-to-external-links-bd3e785325c6
 * https://github.com/wizdmio/wizdm/blob/master/redirect/src/lib/redirect.service.ts
*/

export interface RedirectionExtras extends NavigationExtras {
	target?: string;
}

@Injectable({
	providedIn: 'root'
})
export class RedirectService implements CanActivate {

	constructor(
		readonly router: Router, 
		@Inject(DOCUMENT) readonly document: Document,
		//private route: ActivatedRoute
	) { }

	/** The Window object from Document defaultView */
	get window(): Window { return this.document.defaultView; }

	/** Jumps instantly to the external link without the mediation of the router */
	public jump(url: string, target: string = '_blank'): Promise<boolean> {
		//console.log('RedirectService.jump().url', url);
		return new Promise<boolean>( (resolve, reject) => {

			try { resolve(!!this.window.open(url, target)); }
			catch(e) { reject(e); }
		});
	}

	/** Returns true if the given url looks external */
	public external(url: string): boolean {
		if(url) url = url.toLowerCase();
		const isExternal = /^http(?:s)?:\/{2}\S+$/.test(url);
		//console.log('RedirectService.external().url', url, isExternal);
		//return /^http(?:s)?:\/{2}\S+$/.test(url);
		return isExternal;
	}

	/** Redirects to the specified external link with the mediation of the router */
	public redirect(url: string, extras?: RedirectionExtras): Promise<boolean> {
		//console.log('RedirectService.redirect().url', url, extras);
		// Extracts the target from the extras
		const target = extras && extras.target;
		// Compose the url link for redirection
		const link = "/redirect?url=" + url + (!!target ? "&=" + target : '');
		// Navigates with the router activat the redirection guard
		return this.router.navigateByUrl(link, extras);
	}

	/** Navigates to the given url, redirecting when necessary 
	 * @param url An absolute URL. The function does not apply any delta to the current URL. 
	 * When starting with 'http(s)://' triggers the external redirection. 
	 * @param extras (optional). An object containing properties that modify the navigation strategy. 
	 * The function ignores any properties that would change the provided URL. 
	 */
	public navigate(url: string, extras?: RedirectionExtras): Promise<boolean> {
		//console.log('RedirectService.navigate().url', url, extras);
		return this.external(url) ? 
			// Redirects to external link
			//this.redirect(url, extras) : 
			this.jump(url) : //^^ the redirect stuff didn't work. it didnt' like the /redirect route
			// Navigates with the router otherwise 
			this.router.navigateByUrl(url, extras);
	}

	canActivate(route: ActivatedRouteSnapshot) {
		//console.log('RedirectService.canActivate().route', route);
		// Gets the url query parameter, if any
		const url = route.queryParamMap.get('url');
		//console.log('RedirectService.canActivate().route.param.url:', url);
		// If the url matches an external link, redirects stopping the route activation
		if( this.external(url) ) { 
			// Gets the optional target, when specified
			const target = route.queryParamMap.get('target');
			// Jumps to the external resource
			return this.jump(url, target).then(() => false); 
		}
		// Goes on activating the requested route, most likely to NotFound
		return true;
	}
}